import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { scenarioTestingApi } from '../../../apis';
import { InternalApiScenarioTestResultStatus } from '../../../api-internal';
import SalvPage from '../../../common/SalvPage';
import { useMe } from '../../../organisation/useMe';
import { useQueryScenario } from '../../scenarioQueryActions';
import Editor from '../../../common/editor/Editor';
import { TestRunResults } from './TestRunResults';
import './TestRun.scss';
import { TestRunAttributesCard } from './TestRunAttributesCard';
import { TestRunDetailsCard } from './TestRunDetailsCard';
import { TestRunStatusSection } from './TestRunStatusSection';
import { SalvCard } from '@salvapp/ui-components';
import { ExpansionOptions } from '@salvapp/ui-components/build/SalvCard/SalvCard.types';

export const TestRun = (): React.ReactElement | null => {
  const { testRunHandle, handle, contentHandle } = useParams<{
    testRunHandle: string;
    handle: string;
    contentHandle: string;
  }>();

  const [isQueryExpanded, setQueryExpanded] = useState<boolean>(false);

  const { currentOrganisation } = useMe();

  const { data: testResult } = useQuery({
    queryKey: ['testRunInformation', testRunHandle, handle, contentHandle, currentOrganisation?.id],
    queryFn: async () =>
      scenarioTestingApi.getScenarioContentTestResults({
        testHandle: testRunHandle!,
        scenarioHandle: handle!,
        contentHandle: contentHandle!,
      }),
  });

  const { data: scenario } = useQueryScenario(handle!);

  const getExpansionOptions = (): ExpansionOptions => {
    return {
      isExpanded: isQueryExpanded,
      onToggle: () => setQueryExpanded((prev) => !prev),
    };
  };

  const versionQuery = () => {
    return (
      testResult?.versionInfo && (
        <SalvCard expansionOptions={getExpansionOptions()}>
          <SalvCard.Header className='query-card-header'>Scenario query</SalvCard.Header>
          <SalvCard.Body>
            <Editor
              disabled
              value={testResult.versionInfo.query}
              type={testResult.versionInfo.language}
              onChange={() => {}}
            />
          </SalvCard.Body>
        </SalvCard>
      )
    );
  };

  return (
    <SalvPage
      title={'Test Run Results'}
      breadcrumb={{
        title: 'Scenario',
        to: { pathname: `/scenarios/${handle}`, search: 'tab=test-runs' },
      }}
    >
      {testResult && scenario && (
        <div>
          <TestRunStatusSection testResult={testResult} scenario={scenario} />
          <TestRunDetailsCard testResult={testResult} scenario={scenario} />
          <TestRunAttributesCard testResult={testResult} scenario={scenario} />

          <div className='m-t-24'>{versionQuery()}</div>

          {testResult.status === InternalApiScenarioTestResultStatus.FINISHED && (
            <TestRunResults testResult={testResult} scenario={scenario} />
          )}
        </div>
      )}
    </SalvPage>
  );
};

export default TestRun;
